import { layerMetadataAtom } from '@app/domains/layers/state/layers.atoms'
import { getBaseLayerId } from '@app/domains/layers/utils/layers.utils'
import { LayerMetadataRecord } from '@app/types'
import { useAtomValue } from 'jotai'
import { useMemo } from 'react'

/**
 * Returns the layer information and data quality details for a layer (hazard, element, or contextual).
 *
 * Strips the asset tag to the base layer id (e.g. "h164_l0_v3" → "h164")
 * and looks it up in `layerMetadataAtom`.
 *
 * Returns the full `LayerMetadataRecord` object including owner, confidence rating,
 * description, limitations, source links, and suitability scores.
 * Returns `null` if no metadata is found for the given id.
 *
 * @param id - The asset tag of the layer (e.g. "h164_l0_v3")
 */
export const useLayerMetadata = (id?: string): LayerMetadataRecord | null => {
  const layerMetadata = useAtomValue(layerMetadataAtom)

  return useMemo(() => {
    if (!id) return null
    const baseLayerId = getBaseLayerId(id)
    return layerMetadata.find((m) => m.id === baseLayerId) ?? null
  }, [id, layerMetadata])
}
